import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// Apple devices (iPhone, iPad, Mac) start on the iPhone page; Android and
// everything else (Windows, Linux…) on Android, where the live apps are.
function defaultPage(pages) {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const isApple = /iPhone|iPad|iPod|Macintosh|Mac OS X/.test(ua) && !/Android/.test(ua);
  const key = isApple ? "ios" : "android";
  return Math.max(0, pages.findIndex((p) => p.key === key));
}

// Horizontal scroll-snap pager: `go(i)` scrolls to a page, swiping updates
// `index`. Works in RTL (scrollLeft is negative there).
export default function useHomePager(pages) {
  const ref = useRef(null);
  const [index, setIndex] = useState(() => defaultPage(pages));
  // Page a go() call is smooth-scrolling to. While set, scroll events must not
  // touch `index`, or the switch flicks back to the old page mid-animation.
  const target = useRef(null);
  const settle = useRef(0);

  const scrollTo = useCallback((i, behavior) => {
    const el = ref.current;
    if (!el) return;
    const dir = getComputedStyle(el).direction === "rtl" ? -1 : 1;
    el.scrollTo({ left: dir * i * el.clientWidth, behavior });
  }, []);

  useLayoutEffect(() => {
    scrollTo(index, "auto");
    // only on mount — later changes go through go()/onScroll
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const pos = Math.abs(el.scrollLeft) / el.clientWidth;
        if (target.current !== null) {
          if (Math.abs(pos - target.current) > 0.01) return;
          target.current = null;
        }
        setIndex(Math.round(pos));
      });
    };
    // A finger on the pager takes over from any programmatic scroll.
    const release = () => { target.current = null; };
    const onResize = () => scrollTo(Math.round(Math.abs(el.scrollLeft) / el.clientWidth), "auto");
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("touchstart", release, { passive: true });
    el.addEventListener("wheel", release, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settle.current);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("touchstart", release);
      el.removeEventListener("wheel", release);
      window.removeEventListener("resize", onResize);
    };
  }, [scrollTo]);

  const go = useCallback((i) => {
    target.current = i;
    // Safety net if the smooth scroll gets interrupted and never lands.
    clearTimeout(settle.current);
    settle.current = setTimeout(() => { target.current = null; }, 1000);
    setIndex(i);
    scrollTo(i, "smooth");
  }, [scrollTo]);

  return { ref, index, go };
}
